import type { PrizeDrop } from "@/campaigns/modules/prizedrop/types";
import { PrizeRevealCard } from "@/components/prize-reveal/prize-reveal-card";
import { campaignKey } from "@/campaigns/campaign-key";
import { reelPrizes } from "@/campaigns/modules/prizedrop/types";
import { useAppStore } from "@/stores";
import { useIsDesktop } from "@/hooks/use-media-query";

/**
 * The prize-drop detail view's "You have won!" card — the in-widget reveal
 * entry point (the only one for casinos without a game-provider integration,
 * where the in-game launcher button never mounts).
 *
 * Subscribes to the win seam itself rather than taking the win as a prop:
 * `detailView` is rebuilt from `CurrentCampaign`, which doesn't carry
 * `unrevealedWins`, so a win arriving mid-session wouldn't re-render the page.
 * Renders nothing when this campaign has no unrevealed win — and the seam stays
 * occupied for a whole committed reveal, so this never unmounts mid-ceremony.
 */
export const PrizeDropRevealCard = ({
  prizeDrop,
  ownsWindowOn,
  className,
}: {
  prizeDrop: PrizeDrop;
  /** The layout on which this mount renders the portalled reveal window (see
   *  `PrizeRevealCardProps.ownsWindow`): phones mount two of these cards (the
   *  notice and the folded-in sidebar), desktop only the sidebar's — so each
   *  mount owns the window on its own layout, and exactly one ever does.
   *  `"always"`: the one mount at every size (the overlay's dock). */
  ownsWindowOn?: "mobile" | "desktop" | "always";
  className?: string;
}) => {
  const hasWin = useAppStore(
    (s) => campaignKey(prizeDrop.campaign) in s.unrevealedWins
  );
  const isDesktop = useIsDesktop();
  const ownsWindow =
    ownsWindowOn === "always" ||
    (ownsWindowOn !== undefined && (ownsWindowOn === "desktop") === isDesktop);
  if (!hasWin) return null;

  return (
    <PrizeRevealCard
      campaign={prizeDrop.campaign}
      reveal={{ prizes: reelPrizes(prizeDrop) }}
      ownsWindow={ownsWindow}
      className={className}
    />
  );
};
